import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  List, 
  Avatar,
  Tag,
  Button,
  Spin,
  Empty,
  Space,
  Timeline,
  Badge,
  Alert,
  Divider
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import './Dashboard.css';

const { Text, Title } = Typography;

function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({});
  const [todayClasses, setTodayClasses] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'
  const [statusFilter] = useState('all');

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch statistics data
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [studentsResponse, teachersResponse, classesResponse] = await Promise.all([
        axios.get(`${API_URL}/students/count`, { headers }),
        axios.get(`${API_URL}/teachers/count`, { headers }),
        axios.get(`${API_URL}/class-groups/count`, { headers })
      ]);

      setStats({
        totalStudents: studentsResponse.data.total_students,
        totalTeachers: teachersResponse.data.total_teachers,
        totalClasses: classesResponse.data.total_classes,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError(t('fetchStatsError'));
    }
  }, [API_URL, t]);

  // Fetch today's classes
  const fetchTodayClasses = useCallback(async () => {
    try {
      setClassesLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API_URL}/classes/today`, { headers });
      
      // Sort classes by time
      const sortedClasses = response.data.sort((a, b) => {
        return a.schedule_time.localeCompare(b.schedule_time);
      });
      
      setTodayClasses(sortedClasses);
    } catch (error) {
      console.error('Error fetching today\'s classes:', error);
      setError(t('fetchTodayClassesError'));
      setTodayClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, [API_URL, t]);

  // Fetch upcoming courses
  const fetchNewCourses = useCallback(async () => {
    try {
      setCoursesLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/class-groups/upcoming?limit=15`, { headers });
      
      setClassGroups(response.data);
    } catch (error) {
      console.error('Error fetching class groups:', error);
      setError(t('fetchClassGroupsError'));
    } finally {
      setCoursesLoading(false);
    }
  }, [API_URL, t]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchStats(), fetchTodayClasses(), fetchNewCourses()]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [fetchStats, fetchTodayClasses, fetchNewCourses]);

  // Helper functions
  const formatDate = (dateString) => {
    return moment(dateString).format('DD MMM YYYY');
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get filtered classes based on status
  const getFilteredClasses = () => {
    if (statusFilter === 'all') return todayClasses;
    return todayClasses.filter(cls => cls.status === statusFilter);
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    const configs = {
      'approved': { color: 'green', icon: <CheckCircleOutlined />, text: 'Class Taught Approved' },
      'pending': { color: 'warning', icon: <PauseCircleOutlined />, text: 'Class Taught Pending' },
      'default': { color: 'default', icon: <ClockCircleOutlined />, text: 'Unknown' }
    };
    return configs[status] || configs.default;
  };

  // Format time for display
  const formatTime = (timeString) => {
    return moment(timeString, 'HH:mm').format('HH:mm');
  };

  // Calculate end time
  const calculateEndTime = (startTime, duration) => {
    if (!duration) return 'N/A';
    
    let durationMinutes = 0;
    if (typeof duration === 'string') {
      if (duration.includes('h') && duration.includes('m')) {
        const hourMatch = duration.match(/(\d+)h/);
        const minuteMatch = duration.match(/(\d+)m/);
        durationMinutes = (hourMatch ? parseInt(hourMatch[1]) * 60 : 0) + 
                         (minuteMatch ? parseInt(minuteMatch[1]) : 0);
      } else {
        durationMinutes = parseInt(duration) || 60;
      }
    }
    
    return moment(startTime, 'HH:mm').add(durationMinutes, 'minutes').format('HH:mm');
  };

  // Get class statistics
  const getClassStats = () => {
    const total = todayClasses.length;
    const completed = todayClasses.filter(cls => cls.status === 'approved').length;
    const active = todayClasses.filter(cls => ['active', 'ongoing'].includes(cls.status)).length;
    const pending = todayClasses.filter(cls => cls.status === 'pending').length;

    return { total, completed, active, pending };
  };

  const classStats = getClassStats();
  const filteredClasses = getFilteredClasses();

// Render class item for list view
const renderClassItem = (classItem) => {
  const statusConfig = getStatusConfig(classItem.status);
  const endTime = calculateEndTime(classItem.schedule_time, classItem.schedule_hour);
  

  return (
    <List.Item 
      key={classItem.classid}
      style={{ paddingRight: '24px' }}
      extra={
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end',
          gap: '8px',
          minWidth: '120px'
        }}>

          {classItem.student_nicknames && (
            <Badge 
              count={classItem.student_nicknames.split(', ').length} 
              style={{ 
                backgroundColor: '#52c41a',
                fontSize: '10px'
              }}
              title={`${classItem.student_nicknames.split(', ').length} students enrolled`}
            />
          )}
        </div>
      }
    >
      <List.Item.Meta
        avatar={
          <Avatar 
            style={{ 
              backgroundColor: statusConfig.color === 'success' ? '#52c41a' :
                             statusConfig.color === 'processing' ? '#1890ff' :
                             statusConfig.color === 'error' ? '#ff4d4f' :
                             statusConfig.color === 'warning' ? '#faad14' : '#d9d9d9'
            }}
          >
            {classItem.subject_name?.[0] || 'C'}
          </Avatar>
        }
        title={
          <Space wrap>
            <Text strong>{classItem.subject_name || 'Unknown Subject'}</Text>
            <Tag color={statusConfig.color} icon={statusConfig.icon}>
              {statusConfig.text}
            </Tag>
          </Space>
        }
        description={
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space wrap>
              <Text type="secondary">
                <BookOutlined /> {classItem.class_code || 'N/A'}
              </Text>
              <Text type="secondary">
                <UserOutlined /> {classItem.nickname || 'No Teacher'}
              </Text>
            </Space>
            <Space wrap>
              <Text type="secondary">
                <ClockCircleOutlined /> {formatTime(classItem.schedule_time)} - {endTime}
              </Text>
              {classItem.classroom_number && (
                <Text type="secondary">
                  <EnvironmentOutlined /> Room {classItem.classroom_number}
                </Text>
              )}
            </Space>
            {/* Students List */}
            {classItem.student_nicknames && (
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '6px 10px', 
                borderRadius: '4px',
                border: '1px solid #e9ecef',
                marginTop: '4px'
              }}>
                <Space size={4} wrap>
                  <TeamOutlined style={{ color: '#6c757d', fontSize: '12px' }} />
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                    Students:
                  </Text>
                  {classItem.student_nicknames.split(', ').map((student, index) => (
                    <Tag 
                      key={index}
                      size="small"
                      style={{ 
                        margin: '0 2px',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        border: '1px solid #bbdefb',
                        fontSize: '11px'
                      }}
                    >
                      {student.trim()}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </Space>
        }
      />
    </List.Item>
  );
};

// Render timeline view
const renderTimelineView = () => {
  const timelineItems = filteredClasses.map(classItem => {
    const statusConfig = getStatusConfig(classItem.status);
    const endTime = calculateEndTime(classItem.schedule_time, classItem.schedule_hour);
    


    return {
      key: classItem.classid,
      dot: statusConfig.icon,
      color: statusConfig.color === 'processing' ? 'blue' :
             statusConfig.color === 'success' ? 'green' :
             statusConfig.color === 'error' ? 'red' :
             statusConfig.color === 'warning' ? 'orange' : 'gray',
      children: (
        <div style={{ paddingRight: '16px' }}>
          {/* Header with time and status */}
          <div style={{ 
            marginBottom: 8, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <Space>
              <Text strong>{formatTime(classItem.schedule_time)} - {endTime}</Text>
              <Tag color={statusConfig.color} size="small">
                {statusConfig.text}
              </Tag>
            </Space>
          
          </div>

          {/* Subject name */}
          <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
            {classItem.subject_name || 'Unknown Subject'}
          </Title>

          {/* Class details */}
          <Space wrap size={[8, 4]} style={{ marginBottom: 8 }}>
            <Text type="secondary">{classItem.class_code}</Text>
            <Text type="secondary">• {classItem.nickname || 'No Teacher'}</Text>
            {classItem.classroom_number && (
              <Text type="secondary">• Room {classItem.classroom_number}</Text>
            )}
          </Space>

          {/* Students list */}
          {classItem.student_nicknames && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '6px 10px', 
              borderRadius: '4px',
              border: '1px solid #e9ecef',
              marginTop: '8px'
            }}>
              <Space size={4} wrap>
                <TeamOutlined style={{ color: '#6c757d', fontSize: '12px' }} />
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                  Students ({classItem.student_nicknames.split(', ').length}):
                </Text>
                {classItem.student_nicknames.split(', ').map((student, index) => (
                  <Tag 
                    key={index}
                    size="small"
                    style={{ 
                      margin: '0 2px',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      border: '1px solid #bbdefb',
                      fontSize: '11px'
                    }}
                  >
                    {student.trim()}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </div>
      )
    };
  });

  return (
    <Timeline 
      mode="left"
      items={timelineItems}
      style={{ marginTop: 16 }}
    />
  );
};

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        padding: '24px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 24px' }}>
      {/* Header */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <Row align="middle" justify="space-between">
              <Col xs={24} sm={18}>
                <Text type="primary" style={{ fontSize: '24px' }}>
                  {t('Today\'s overview')} - {moment().format('dddd, DD MMMM YYYY')}
                </Text>
              </Col>
              <Col xs={24} sm={6} style={{ textAlign: 'right', marginTop: window.innerWidth < 576 ? '12px' : '0' }}>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={() => {
                    fetchStats();
                    fetchTodayClasses();
                    fetchNewCourses();
                  }}
                  loading={classesLoading || coursesLoading}
                >
                  {t('Refresh')}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Statistics Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Students')}
              value={stats.totalStudents}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Teachers')}
              value={stats.totalTeachers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Courses')}
              value={stats.totalClasses}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={24} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Today\'s Classes')}
              value={classStats.total}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: '20px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Classes and Courses Section - Side by Side */}
      <Row gutter={[16, 16]}>
        {/* Today's Classes - Left Side */}
        <Col xs={24} lg={14}>
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                <Text strong>{t('Today\'s Classes Schedule')}</Text>
                <Badge count={filteredClasses.length} />
              </Space>
            }
            extra={
              <Space wrap>
                <Button.Group size="small">
                  <Button 
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => setViewMode('list')}
                    icon={<FilterOutlined />}
                  >
                    {t('List')}
                  </Button>
                  <Button 
                    type={viewMode === 'timeline' ? 'primary' : 'default'}
                    onClick={() => setViewMode('timeline')}
                    icon={<ClockCircleOutlined />}
                  >
                    {t('Timeline')}
                  </Button>
                </Button.Group>
              </Space>
            }
            loading={classesLoading}
          >
            {error && (
              <Alert 
                message={error} 
                type="error" 
                style={{ marginBottom: 16 }} 
                closable 
              />
            )}
            
            {filteredClasses.length === 0 ? (
              <Empty 
                description={
                  statusFilter === 'all' 
                    ? t('No classes scheduled for today')
                    : t('No classes found for selected status')
                }
                image={Empty.SIMPLE_MODE}
              />
            ) : (
              <div>
                {viewMode === 'list' ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={filteredClasses}
                    renderItem={renderClassItem}
                    size="small"
                    style={{ 
                      maxHeight: '600px', 
                      overflowY: 'auto' 
                    }}
                  />
                ) : (
                  <div style={{ 
                    maxHeight: '600px', 
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}>
                    {renderTimelineView()}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>

        {/* Upcoming Courses - Right Side */}
        <Col xs={24} lg={10}>
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                <Text strong>
                  {t('Next 15 Class Groups')} ({classGroups.length}/15)
                </Text>
              </Space>
            }
            loading={coursesLoading}
          >
            <List
              itemLayout="vertical"
              dataSource={classGroups.filter(cls => !cls.isprivate)}
              locale={{ emptyText: <Empty description={t('No classes available')} /> }}
              style={{ 
                maxHeight: '600px', 
                overflowY: 'auto' 
              }}
              renderItem={cls => (
                <List.Item
                  extra={
                    <Tag color="processing">
                      {formatMinutesToHours(cls.total_hours)}
                    </Tag>
                  }
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{cls.class_code}</Text>
                        <Divider type="vertical" />
                        <Text type="secondary">
                          {formatDate(cls.start_date)} - {formatDate(cls.end_date)}
                        </Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Space>
                          <InfoCircleOutlined />
                          <Text>{cls.subject_name}</Text>
                        </Space>
                        <Space>
                          <TeamOutlined />
                          <Text>{cls.nickname}</Text>
                        </Space>
                        <Space>
                          <UserOutlined />
                          <Text>{t('registerCapacity')}: {cls.register_capacity}</Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default withRoleAccess(AdminDashboard, ['admin', 'superadmin']);